import { useState, useEffect, useRef } from "react";
import { Camera, Upload, X, Play, Trash2, Lock, Globe, Loader2, Image as ImageIcon, ChevronLeft, ChevronRight, Download, FolderDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders, getAuthToken } from "@/lib/firebase";
import heic2any from "heic2any";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const EventGallery = ({ gameId, gameTitle, isOwner, isAttendee, isPastEvent }) => {
  const { user } = useAuth();
  const [gallery, setGallery] = useState([]);
  const [visibility, setVisibility] = useState("public");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const fileInputRef = useRef(null);

  // Get current media from index
  const selectedMedia = selectedIndex !== null ? gallery[selectedIndex] : null;

  // Show upload button if past event OR user is owner/attendee
  const showUploadButton = isPastEvent || isOwner || isAttendee;
  // Can actually upload if logged in AND (owner, attendee, or past event)
  const canUpload = user && (isOwner || isAttendee || isPastEvent);
  const canChangeSettings = isOwner;

  useEffect(() => {
    fetchGallery();
  }, [gameId]);

  const fetchGallery = async () => {
    try {
      const headers = user ? await authHeaders() : {};
      const response = await fetch(`${API}/games/${gameId}/gallery`, { headers });
      if (response.ok) {
        const data = await response.json();
        setGallery(data.media || []);
        setVisibility(data.visibility || "public");
      }
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === "ArrowLeft") {
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        goToNext();
      } else if (e.key === "Escape") {
        setSelectedIndex(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, gallery.length]);

  const goToPrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedIndex < gallery.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handleDownload = async (media) => {
    try {
      const response = await fetch(media.file_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = media.file_type === "video" ? "mp4" : "jpg";
      a.download = `${gameTitle?.replace(/[^a-z0-9]/gi, '_') || 'photo'}_${media.media_id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download");
    }
  };

  const handleDownloadAll = async () => {
    if (gallery.length === 0) return;
    
    setDownloadingAll(true);
    const toastId = toast.loading(`Preparing ${gallery.length} files...`);
    
    try {
      // Check if on iOS/mobile with Web Share API support for files
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const canShareFiles = navigator.canShare && navigator.share;
      
      // Fetch all files first
      const files = [];
      let completed = 0;
      
      for (const media of gallery) {
        try {
          const response = await fetch(media.file_url);
          const blob = await response.blob();
          const ext = media.file_type === "video" ? "mp4" : "jpg";
          const filename = `${gameTitle?.replace(/[^a-z0-9]/gi, '_') || 'photo'}_${completed + 1}.${ext}`;
          const file = new File([blob], filename, { type: blob.type });
          files.push(file);
          completed++;
          toast.loading(`Downloading ${completed}/${gallery.length}...`, { id: toastId });
        } catch (err) {
          console.error(`Failed to download ${media.media_id}:`, err);
        }
      }
      
      // On iOS, try to use Web Share API so users can save to Photos
      if (isIOS && canShareFiles && files.length > 0) {
        try {
          // Check if we can share these files
          if (navigator.canShare({ files })) {
            toast.loading("Opening share menu...", { id: toastId });
            await navigator.share({
              files,
              title: `${gameTitle || 'Event'} Photos`,
            });
            toast.success(`${completed} files ready to save!`, { id: toastId });
            setDownloadingAll(false);
            return;
          }
        } catch (shareErr) {
          // User cancelled or share failed - fall back to ZIP
          if (shareErr.name === 'AbortError') {
            toast.dismiss(toastId);
            setDownloadingAll(false);
            return;
          }
          console.log("Share failed, falling back to ZIP:", shareErr);
        }
      }
      
      // Fallback: Create ZIP file (desktop or share not supported)
      toast.loading("Creating zip file...", { id: toastId });
      const zip = new JSZip();
      const folderName = gameTitle?.replace(/[^a-z0-9]/gi, '_') || 'event';
      const folder = zip.folder(folderName);
      
      for (let i = 0; i < files.length; i++) {
        folder.file(files[i].name, files[i]);
      }
      
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${folderName}_photos.zip`);
      
      toast.success(`Downloaded ${completed} files!`, { id: toastId });
    } catch (error) {
      console.error("Bulk download failed:", error);
      toast.error("Failed to create download", { id: toastId });
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (let file of files) {
      try {
        // Convert HEIC/HEIF to JPEG on the frontend (for iPhone photos)
        const isHeic = file.type === "image/heic" || file.type === "image/heif" || 
                       file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");
        
        if (isHeic) {
          toast.info("Converting iPhone photo...");
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.85
            });
            // heic2any can return array or single blob
            const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            file = new File([jpegBlob], file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"), { type: "image/jpeg" });
          } catch (convErr) {
            console.error("HEIC conversion error:", convErr);
            toast.error("Failed to convert iPhone photo. Try taking a screenshot instead.");
            continue;
          }
        }

        const formData = new FormData();
        formData.append("file", file);

        const token = await getAuthToken();
        
        if (!token) {
          toast.error("Please login to upload photos");
          setUploading(false);
          return;
        }
        
        // Use XMLHttpRequest to avoid fetch body stream issues
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", `${API}/games/${gameId}/gallery`);
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              successCount++;
              resolve(JSON.parse(xhr.responseText));
            } else {
              // Parse error message from response
              let errorMsg = "Upload failed";
              try {
                const errData = JSON.parse(xhr.responseText);
                errorMsg = errData.detail || errData.message || errorMsg;
              } catch (e) {
                errorMsg = xhr.responseText || errorMsg;
              }
              console.error("Upload error response:", xhr.status, xhr.responseText);
              reject(new Error(errorMsg));
            }
          };
          xhr.onerror = () => {
            console.error("XHR error:", xhr.status, xhr.statusText);
            reject(new Error("Network error - please try again"));
          };
          xhr.send(formData);
        });
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Failed to upload ${file.name}: ${error.message}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? "s" : ""} uploaded!`);
      fetchGallery();
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (mediaId) => {
    setDeleting(mediaId);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API}/games/${gameId}/gallery/${mediaId}`, {
        method: "DELETE",
        headers
      });
      if (response.ok) {
        const newGallery = gallery.filter(m => m.media_id !== mediaId);
        setGallery(newGallery);
        toast.success("Deleted");
        // Close dialog or move to next/previous
        if (newGallery.length === 0) {
          setSelectedIndex(null);
        } else if (selectedIndex >= newGallery.length) {
          setSelectedIndex(newGallery.length - 1);
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to delete");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete");
    } finally {
      setDeleting(null);
    }
  };

  const handleVisibilityChange = async (newVisibility) => {
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const response = await fetch(`${API}/games/${gameId}/gallery/settings`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ visibility: newVisibility })
      });
      if (response.ok) {
        setVisibility(newVisibility);
        toast.success(`Gallery is now ${newVisibility === "public" ? "public" : "private"}`);
      }
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?";

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  // Don't show gallery section if event hasn't happened yet and no uploads
  if (!isPastEvent && gallery.length === 0 && !canUpload) {
    return null;
  }

  return (
    <div className="p-4 rounded-xl bg-card border border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Gallery</h3>
          {gallery.length > 0 && (
            <span className="text-sm text-muted-foreground">({gallery.length})</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {showUploadButton && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleUpload}
              />
              <Button
                size="sm"
                onClick={() => {
                  if (!user) {
                    toast.error("Please login to upload photos");
                    return;
                  }
                  if (!canUpload) {
                    toast.error("Only attendees can upload photos");
                    return;
                  }
                  fileInputRef.current?.click();
                }}
                disabled={uploading}
                className="h-8"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><Upload className="w-4 h-4 mr-1" />Add</>
                )}
              </Button>
            </>
          )}
          
          {gallery.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadAll}
              disabled={downloadingAll}
              className="h-8"
            >
              {downloadingAll ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><FolderDown className="w-4 h-4 mr-1" />Download</>
              )}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : gallery.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No photos yet</p>
          {canUpload && isPastEvent && (
            <p className="text-xs mt-1">Be the first to share photos from this event!</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {gallery.map((media, index) => (
            <button
              key={media.media_id}
              onClick={() => setSelectedIndex(index)}
              className="relative aspect-square rounded-lg overflow-hidden bg-muted group"
            >
              {media.file_type === "video" ? (
                <>
                  <video src={media.file_url} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="w-8 h-8 text-white" fill="white" />
                  </div>
                </>
              ) : (
                <img src={media.file_url} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
            </button>
          ))}
        </div>
      )}

      {/* Media Viewer Dialog with navigation */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {selectedMedia && (
            <>
              <div className="relative bg-black aspect-square sm:aspect-video">
                {selectedMedia.file_type === "video" ? (
                  <video
                    key={selectedMedia.media_id}
                    src={selectedMedia.file_url}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <img
                    key={selectedMedia.media_id}
                    src={selectedMedia.file_url}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                )}
                
                {/* Left arrow */}
                {selectedIndex > 0 && (
                  <button
                    onClick={goToPrevious}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                )}
                
                {/* Right arrow */}
                {selectedIndex < gallery.length - 1 && (
                  <button
                    onClick={goToNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                )}
                
                {/* Counter */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full text-white text-sm">
                  {selectedIndex + 1} / {gallery.length}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={selectedMedia.user_picture} />
                    <AvatarFallback className="bg-primary/20 text-primary text-xs">
                      {getInitials(selectedMedia.user_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium">{selectedMedia.user_name}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(selectedMedia.created_at)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Download button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(selectedMedia)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                  
                  {/* Delete button */}
                  {user && (selectedMedia.user_id === user.user_id || isOwner) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDelete(selectedMedia.media_id)}
                      disabled={deleting === selectedMedia.media_id}
                    >
                      {deleting === selectedMedia.media_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Trash2 className="w-4 h-4 mr-1" />Delete</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventGallery;
